import { useState, useMemo } from 'react';
import { mockProducts } from '../../../mocks/products';
import { ProductCard } from './ProductCard';
import { ProductListItem } from './ProductListItem';

interface ProductGridProps {
  selectedCategory: string;
  selectedRegion: string;
  priceRange: string;
  searchQuery: string;
  viewMode: 'grid' | 'list';
}

export function ProductGrid({
  selectedCategory,
  selectedRegion,
  priceRange,
  searchQuery,
  viewMode,
}: ProductGridProps) {
  const [sortBy, setSortBy] = useState<string>('popular');

  const filteredProducts = useMemo(() => {
    let filtered = [...mockProducts];

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Region filter
    if (selectedRegion !== 'all') {
      filtered = filtered.filter((p) => p.region === selectedRegion);
    }

    // Price range filter
    if (priceRange !== 'all') {
      filtered = filtered.filter((p) => {
        switch (priceRange) {
          case 'under-100k':
            return p.price < 100000;
          case '100k-500k':
            return p.price >= 100000 && p.price < 500000;
          case '500k-1m':
            return p.price >= 500000 && p.price < 1000000;
          case '1m-5m':
            return p.price >= 1000000 && p.price < 5000000;
          case 'over-5m':
            return p.price >= 5000000;
          default:
            return true;
        }
      });
    }

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.manufacturer.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
      default:
        filtered.sort((a, b) => b.reviews - a.reviews);
        break;
    }

    return filtered;
  }, [selectedCategory, selectedRegion, priceRange, searchQuery, sortBy]);

  return (
    <div>
      {/* Results Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            검색 결과 <span className="text-ocean-600">{filteredProducts.length}</span>개
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            전국 우수 제조업체의 검증된 제품을 만나보세요
          </p>
        </div>

        {/* Sort Dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">정렬:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500 text-sm cursor-pointer"
          >
            <option value="popular">인기순</option>
            <option value="rating">평점순</option>
            <option value="price-low">낮은 가격순</option>
            <option value="price-high">높은 가격순</option>
          </select>
        </div>
      </div>

      {/* Products Display */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-20">
          <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            검색 결과가 없습니다
          </h3>
          <p className="text-gray-600">
            다른 검색어나 필터를 시도해보세요
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map((product) => (
            <ProductListItem key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
}